import { useRef, useEffect } from 'react';
import { useFocusTrap } from '../lib/use-focus-trap.js';

/**
 * Below 768 px the menu button opens the sidebar as this drawer, with the
 * customer and app switchers inside (portal workbench spec). A modal dialog:
 * it traps focus while open and closes on Escape.
 */
export default function NavDrawer({ open, onClose, returnFocusRef = null, children }) {
  const panelRef = useRef(null);
  const onCloseRef = useRef(onClose);

  // Update onCloseRef first, so other effects can use it
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // The backdrop's own onClick closes the drawer; a mousedown close as well
  // would remove it before the click and risk an iOS ghost click.
  useFocusTrap(panelRef, { active: open, onClose, returnFocusRef, closeOnOutsidePointer: false });

  // Close drawer when viewport widens past 768px
  useEffect(() => {
    if (!open || typeof window.matchMedia !== 'function') return;

    const mq = window.matchMedia('(min-width: 768px)');
    if (mq.matches) {
      onCloseRef.current();
      return;
    }

    const handleChange = (e) => {
      if (e.matches) {
        onCloseRef.current();
      }
    };

    mq.addEventListener('change', handleChange);
    return () => mq.removeEventListener('change', handleChange);
  }, [open]);

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 md:hidden">
      <div data-testid="nav-drawer-backdrop" aria-hidden="true" className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div
        ref={panelRef}
        id="nav-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
        className="absolute inset-y-0 left-0 flex w-80 max-w-[85vw] flex-col overflow-y-auto overscroll-contain bg-white text-ipc-text shadow-xl"
      >
        <div className="flex items-center justify-between border-b border-ipc-border px-4 py-3">
          <p className="text-sm font-semibold text-ipc-primary">Menu</p>
          <button type="button" aria-label="Close menu" onClick={onClose} className="rounded p-2 hover:bg-slate-100">
            <svg aria-hidden="true" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
        <div className="flex-1 space-y-6 p-4">{children}</div>
      </div>
    </div>
  );
}
